import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOwnerBookings } from 'actions/account/ownerBookingsSection';
import { fetchStorageSites } from 'actions/account/storageSitesSection';
import strings from 'localization/strings';
import queryString from 'query-string';
import { getTabs, isStale, parseFilterParams } from './BookingsSectionHelper';
import routes from 'routes';
import { handleResponse } from 'actions/actionHelpers';
import messageTypes from 'enums/messageTypes';

import BookingsSection from './BookingsSection';
import OwnerBookingListItem from './OwnerBookingListItem';
import BookingFilter from './BookingFilter';
import SendMessageToTenantsDialog from './SendMessageToTenantsDialog';
import TenantMessageTemplateLogDialog from './TenantMessageTemplateLogDialog';
import ContextMenu from 'common/ContextMenu';
import MenuItem from '@material-ui/core/MenuItem';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';

const OwnerBookingsSection = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const [bookingsResult, setBookingsResult] = useState({});
    const [bookingsFetchedTime, setBookingsFetchedTime] = useState(undefined);
    const [isLoadingBookings, setIsLoadingBookings] = useState(undefined);
    const [bookingsLoadingError, setBookingsLoadingError] = useState(undefined);
    const [sendMessageToTenantsDialogOpen, setSendMessageToTenantsDialogOpen] = useState(false);
    const [tenantMessageTemplateLogDialogOpen, setTenantMessageTemplateLogDialogOpen] = useState(false);
    const [sendMessageToTenantsDialogMessageType, setSendMessageToTenantsDialogMessageType] = useState(undefined);

    const authenticationContext = useSelector(state => state.authentication.context);
    const { selectedActor } = authenticationContext;
    const storageSites = useSelector(state => state.account.storageSitesSection.storageSites);
    const isLoadingStorageSites = useSelector(state => state.account.storageSitesSection.isLoading);
    const isLoading = isLoadingBookings || isLoadingStorageSites;
    const storageSitesLoadingError = useSelector(state => state.account.storageSitesSection.loadingError);
    const loadingError = !!(bookingsLoadingError || storageSitesLoadingError);

    const filterParams = parseFilterParams(location);
    const filterQueryString = queryString.stringify(filterParams);
    const initialBookingFilterIsExpanded = !!(filterParams.storageSiteId || filterParams.q);
    if(storageSites?.length === 1) {
        filterParams.storageSiteId = storageSites[0].id;
    }

    const [bookingFilterExpanded, setBookingFilterExpanded] = useState(initialBookingFilterIsExpanded);

    useEffect(() => {
        history.push(`${routes.account.ownerBookings}?${filterQueryString}`);
        fetchBookings(true);
    }, [filterQueryString]);

    useEffect(() => {
        if(isStale(bookingsFetchedTime)) {
            fetchBookings(false);
        }
    }, [isStale(bookingsFetchedTime)]);

    // reload every other minute, but without showing the loading indicator
    useEffect(() => {
        const timer = window.setInterval(() => fetchBookings(false), 120000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if(!storageSites) {
            dispatch(fetchStorageSites(selectedActor.id));
        }
    }, [storageSites]);

    const fetchBookings = isLoadingValue => {
        setIsLoadingBookings(isLoadingValue);
        setBookingsLoadingError(undefined);
        const request = {
            ownerActorId: selectedActor.id,
            adminBookingStatuses: filterParams.adminBookingStatuses,
            storageSiteId: filterParams.storageSiteId,
            q: filterParams.q,
            page: filterParams.page - 1,
            pageSize: filterParams.pageSize
        };
        dispatch(fetchOwnerBookings(request))
            .then(handleResponse(
                response => {
                    setBookingsResult(response.payload);
                    const now = new Date().getTime();
                    setBookingsFetchedTime(now);
                    setIsLoadingBookings(false);
                },
                response => {
                    setBookingsLoadingError(response);
                    setIsLoadingBookings(false);
                }
            ));
    };

    const handleFilterChange = changedFilterParams => {
        if(changedFilterParams.bookingId) {
            history.push(routes.account.ownerBookingDetails.replace(':bookingId', changedFilterParams.bookingId));
        } else {
            const newFilterParams = { ...filterParams, ...changedFilterParams };
            const newFilterQueryString = queryString.stringify(newFilterParams);
            history.push(`${routes.account.ownerBookings}?${newFilterQueryString}`);
        }
    };

    const handleOpenSendMessageToTenantsDialog = (messageType, handleCloseMenu) => {
        handleCloseMenu();
        setSendMessageToTenantsDialogMessageType(messageType);
        setSendMessageToTenantsDialogOpen(true);
    };

    const handleCloseSendMessageToTenantsDialog = () => {
        setSendMessageToTenantsDialogOpen(false);
    };

    const handleOpenTenantMessageTemplateLogDialog = (handleCloseMenu) => {
        handleCloseMenu();
        setTenantMessageTemplateLogDialogOpen(true);
    };

    const handleCloseTenantMessageTemplateLogDialog = () => {
        setTenantMessageTemplateLogDialogOpen(false);
    };

    const handleFilterExpandedChange = () => {
        setBookingFilterExpanded(!bookingFilterExpanded);
    };

    const title = selectedActor.tenantBookingCount > 0 && selectedActor.isOwner
        ? strings.accountTabs.ownerBookings
        : strings.accountTabs.bookings;

    const titleActionPanel = authenticationContext?.isSuperAdmin
        ? (
            <ContextMenu button={<EmailOutlinedIcon/>}>
                {
                    ({ handleCloseMenu }) => (
                        <>
                            <MenuItem onClick={() => handleOpenSendMessageToTenantsDialog(messageTypes.email.key, handleCloseMenu)} disabled={!filterParams.storageSiteId}>{strings.sendEmailToTenants}</MenuItem>
                            <MenuItem onClick={() => handleOpenSendMessageToTenantsDialog(messageTypes.sms.key, handleCloseMenu)} disabled={!filterParams.storageSiteId}>{strings.sendSmsToTenants}</MenuItem>
                            <MenuItem onClick={() => handleOpenTenantMessageTemplateLogDialog(handleCloseMenu)}>{strings.openTenantMessageTemplateLogDialog}</MenuItem>
                        </>
                    )
                }
            </ContextMenu>
        )
        : undefined;

    const bookingFilter = (
        <BookingFilter
            storageSites={storageSites}
            filterParams={filterParams}
            onFilterChange={handleFilterChange}
            onExpandedChange={handleFilterExpandedChange}
            expanded={bookingFilterExpanded}
        />
    );

    const tabs = getTabs(true /* excludeCanceledBookings */);

    const storageSite = storageSites?.find(o => o.id === filterParams.storageSiteId);

    return (
        <>
            <BookingsSection
                storageSites={storageSites}
                bookingsResult={bookingsResult}
                isLoading={isLoading}
                loadingError={loadingError}
                tabs={tabs}
                filterParams={filterParams}
                onFilterChange={handleFilterChange}
                title={title}
                titleActionPanel={titleActionPanel}
                renderBooking={booking => <OwnerBookingListItem booking={booking}/>}
                bookingFilter={bookingFilter}
            />
            {
                storageSite &&
                (
                    <SendMessageToTenantsDialog
                        storageSite={storageSite}
                        messageType={sendMessageToTenantsDialogMessageType}
                        filterParams={filterParams}
                        open={sendMessageToTenantsDialogOpen}
                        onClose={handleCloseSendMessageToTenantsDialog}
                    />
                )
            }
            <TenantMessageTemplateLogDialog
                storageSiteId={filterParams.storageSiteId}
                open={tenantMessageTemplateLogDialogOpen}
                onClose={handleCloseTenantMessageTemplateLogDialog}
            />
        </>
    );
};

export default OwnerBookingsSection;
